import React, { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { X, Loader2, FileCode, Check, AlertCircle, ChevronDown, ChevronUp, Code, Sparkles } from 'lucide-react';
import api from '../services/api';

const SQLImportModal = ({ 
  isOpen, 
  onClose, 
  onImport,
  bigqueryConnectionId 
}) => {
  const [sqlInput, setSqlInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedStages, setParsedStages] = useState([]);
  const [error, setError] = useState(null);
  const [selectedStages, setSelectedStages] = useState([]);
  const [expandedStage, setExpandedStage] = useState(null);
  
  // Import mode: 'raw' = direct raw SQL, 'parse' = AI parse into visual config
  const [importMode, setImportMode] = useState('raw');
  const [rawTargetTable, setRawTargetTable] = useState('');
  
  if (!isOpen) return null;
  
  // Extract target table name from SQL
  const extractTargetTableFromSql = (sql) => {
    const match = sql.match(/CREATE\s+(?:OR\s+REPLACE\s+)?TABLE\s+`?([^`\s(]+)`?/i);
    if (match) {
      const fullName = match[1];
      const parts = fullName.split('.');
      return parts[parts.length - 1];
    }
    return '';
  };
  
  // Auto-extract target table when SQL changes
  const handleSqlChange = (sql) => {
    setSqlInput(sql);
    if (importMode === 'raw') {
      const extracted = extractTargetTableFromSql(sql);
      if (extracted && !rawTargetTable) {
        setRawTargetTable(extracted);
      }
    }
  };
  
  // Import as Raw SQL (no parsing, direct execution)
  const handleImportRaw = () => {
    if (!sqlInput.trim()) {
      setError('Please paste your SQL statement first');
      return;
    }
    
    const targetTable = rawTargetTable || extractTargetTableFromSql(sqlInput) || 'raw_sql_table';
    
    // Create a Gold stage with raw_sql_mode enabled
    const rawStage = {
      stage_type: 'gold',
      target_table: targetTable,
      raw_sql_mode: true,
      raw_sql: sqlInput.trim(),
      auto_inject_tenant_filter: true,
      source_tables: [],
      fields: [],
      join_conditions: [],
      calculated_fields: [],
      group_by_fields: []
    };
    
    onImport([rawStage]);
    onClose();
  };
  
  const handleParse = async () => {
    if (!sqlInput.trim()) {
      setError('Please paste your SQL statements first');
      return;
    }
    
    setParsing(true);
    setError(null);
    setParsedStages([]);
    
    try {
      const response = await api.parseGoldSQL({ 
        sql: sqlInput,
        bigquery_connection_id: bigqueryConnectionId 
      });
      
      if (response.stages && response.stages.length > 0) {
        setParsedStages(response.stages);
        setSelectedStages(response.stages.map((_, idx) => idx)); // Select all by default
      } else if (response.error) {
        setError(response.error);
      } else {
        setError('Could not parse any valid CREATE TABLE statements');
      }
    } catch (err) {
      console.error('Parse error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to parse SQL');
    } finally {
      setParsing(false);
    }
  };
  
  const toggleStageSelection = (idx) => {
    setSelectedStages(prev => 
      prev.includes(idx) 
        ? prev.filter(i => i !== idx)
        : [...prev, idx]
    );
  };
  
  const handleImport = () => {
    const stagesToImport = parsedStages.filter((_, idx) => selectedStages.includes(idx));
    onImport(stagesToImport);
    onClose();
  };
  
  const exampleSQL = `-- Example: Paste your CREATE TABLE statements here
CREATE OR REPLACE TABLE \`gold_layer.Sales_Summary\`
PARTITION BY period_date
CLUSTER BY tenant_id
AS
SELECT
  tenant_id,
  DATE_TRUNC(sales_date, MONTH) AS period_date,
  SUM(amount) AS total_amount,
  COUNT(*) AS row_count
FROM \`silver_layer.transactions\`
GROUP BY tenant_id, period_date;`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <FileCode className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-semibold">Import Gold Stage from SQL</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-4">
          {/* Import Mode Toggle */}
          <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
            <Label className="text-sm font-medium mr-2">Import Mode:</Label>
            <button
              onClick={() => { setImportMode('raw'); setParsedStages([]); setError(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                importMode === 'raw' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Code className="w-4 h-4" />
              Raw SQL (Recommended)
            </button>
            <button
              onClick={() => { setImportMode('parse'); setParsedStages([]); setError(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                importMode === 'parse' 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Parse with AI
            </button>
          </div>
          
          {/* Mode Description */}
          {importMode === 'raw' ? (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Code className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                <div className="text-sm text-purple-800">
                  <p className="font-medium">Raw SQL Mode</p>
                  <p className="text-purple-600 mt-1">
                    Your SQL will be stored as-is and executed directly when the pipeline runs. 
                    Perfect for complex queries with CTEs, UNION ALL, window functions, etc.
                  </p>
                  <p className="text-purple-600 mt-1">
                    Use <code className="bg-purple-100 px-1 rounded">{'{tenant_id}'}</code> placeholder for multi-tenant filtering.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">AI Parse Mode</p>
                  <p className="text-amber-600 mt-1">
                    AI will parse your SQL into visual configuration (fields, joins, aggregations).
                    Good for simpler queries that you want to edit in the Visual Builder.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* SQL Input */}
          <div className="space-y-2">
            <Label>Paste your CREATE TABLE SQL {importMode === 'raw' ? 'statement' : 'statements'}</Label>
            <textarea
              value={sqlInput}
              onChange={(e) => handleSqlChange(e.target.value)}
              placeholder={exampleSQL}
              className="w-full h-[250px] p-3 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 bg-slate-900 text-slate-100"
              spellCheck={false}
            />
          </div>
          
          {/* Raw SQL Mode - Target Table Name */}
          {importMode === 'raw' && sqlInput.trim() && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Target Table Name</Label>
                <Input
                  value={rawTargetTable}
                  onChange={(e) => setRawTargetTable(e.target.value)}
                  placeholder="Auto-extracted from SQL"
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  {rawTargetTable ? `✓ Table: ${rawTargetTable}` : 'Will be extracted from SQL'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">SQL Analysis</Label>
                <div className="text-xs space-y-1 bg-slate-50 p-2 rounded">
                  {sqlInput.toLowerCase().includes('create') ? (
                    <span className="text-green-600 block">✓ CREATE statement found</span>
                  ) : (
                    <span className="text-amber-600 block">⚠ No CREATE statement</span>
                  )}
                  {sqlInput.includes('{tenant_id}') ? (
                    <span className="text-green-600 block">✓ Tenant placeholder found</span>
                  ) : (
                    <span className="text-slate-400 block">○ No tenant placeholder</span>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Parse Mode - Parse Button */}
          {importMode === 'parse' && (
            <div className="flex justify-center">
              <Button 
                onClick={handleParse} 
                disabled={parsing || !sqlInput.trim()}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {parsing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Parsing with AI...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Parse SQL
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          {/* Parsed Results */}
          {parsedStages.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Parsed {parsedStages.length} Gold Stage{parsedStages.length > 1 ? 's' : ''}
                </Label>
                <span className="text-xs text-muted-foreground">
                  {selectedStages.length} selected for import
                </span>
              </div>
              
              <ScrollArea className="h-[300px] border rounded-lg">
                <div className="p-2 space-y-2">
                  {parsedStages.map((stage, idx) => (
                    <div 
                      key={idx} 
                      className={`border rounded-lg overflow-hidden transition-colors ${
                        selectedStages.includes(idx) ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200 bg-slate-50/50'
                      }`}
                    >
                      {/* Stage Header */}
                      <div 
                        className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-100/50"
                        onClick={() => setExpandedStage(expandedStage === idx ? null : idx)}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedStages.includes(idx)}
                            onChange={() => toggleStageSelection(idx)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                          />
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                            Gold
                          </Badge>
                          <span className="font-medium">{stage.target_table || 'Unnamed'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {(stage.fields || []).length} fields
                          </Badge>
                          {(stage.source_tables || []).length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {(stage.source_tables || []).length} source{(stage.source_tables || []).length > 1 ? 's' : ''}
                            </Badge>
                          )}
                          {expandedStage === idx ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>
                      
                      {/* Stage Details */}
                      {expandedStage === idx && (
                        <div className="px-4 py-3 border-t bg-white space-y-3">
                          {/* Source Tables */}
                          {stage.source_tables && stage.source_tables.length > 0 && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Source Tables</Label>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {stage.source_tables.map((st, stIdx) => (
                                  <Badge key={stIdx} variant="outline" className="text-xs">
                                    {st.source_layer === 'gold' ? 'gold_layer' : 'silver_layer'}.{st.table_name}
                                    {st.alias && ` (${st.alias})`}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Fields Preview */}
                          {stage.fields && stage.fields.length > 0 && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Output Fields</Label>
                              <div className="mt-1 text-xs font-mono bg-slate-100 p-2 rounded max-h-[100px] overflow-auto">
                                {stage.fields.map((f, fIdx) => (
                                  <div key={fIdx} className="py-0.5">
                                    {f.custom_expression || f.source_field || '?'} → <strong>{f.target_field}</strong>
                                    {f.aggregation && f.aggregation !== 'none' && (
                                      <span className="text-amber-600 ml-1">({f.aggregation})</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Calculated Fields */}
                          {stage.calculated_fields && stage.calculated_fields.length > 0 && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Calculated Fields</Label>
                              <div className="mt-1 text-xs font-mono bg-slate-100 p-2 rounded">
                                {stage.calculated_fields.map((cf, cfIdx) => (
                                  <div key={cfIdx} className="py-0.5">
                                    <strong>{cf.name}</strong> = {cf.expression}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Join Conditions */}
                          {stage.join_conditions && stage.join_conditions.length > 0 && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Join Conditions</Label>
                              <div className="mt-1 text-xs font-mono bg-slate-100 p-2 rounded">
                                {stage.join_conditions.map((jc, jcIdx) => (
                                  <div key={jcIdx} className="py-0.5">
                                    {jc.join_type || 'JOIN'}: {jc.left_table}.{jc.left_field} = {jc.right_table}.{jc.right_field}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* GROUP BY */}
                          {stage.group_by_fields && stage.group_by_fields.length > 0 && (
                            <div>
                              <Label className="text-xs text-muted-foreground">GROUP BY</Label>
                              <div className="mt-1 text-xs font-mono bg-slate-100 p-2 rounded">
                                {stage.group_by_fields.join(', ')}
                              </div>
                            </div>
                          )}
                          
                          {/* Partitioning & Clustering */}
                          <div className="flex gap-4">
                            {stage.partition_by && (
                              <div>
                                <Label className="text-xs text-muted-foreground">Partition By</Label>
                                <div className="text-xs font-mono">{stage.partition_by}</div>
                              </div>
                            )}
                            {stage.cluster_by && (
                              <div>
                                <Label className="text-xs text-muted-foreground">Cluster By</Label>
                                <div className="text-xs font-mono">{stage.cluster_by}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-3 shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          
          {/* Raw SQL Mode - Import Button */}
          {importMode === 'raw' && (
            <Button 
              onClick={handleImportRaw}
              disabled={!sqlInput.trim()}
              className="bg-purple-500 hover:bg-purple-600"
            >
              <Code className="w-4 h-4 mr-2" />
              Import as Raw SQL
            </Button>
          )}
          
          {/* Parse Mode - Import Button */}
          {importMode === 'parse' && (
            <Button 
              onClick={handleImport}
              disabled={parsedStages.length === 0 || selectedStages.length === 0}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Check className="w-4 h-4 mr-2" />
              Import {selectedStages.length} Stage{selectedStages.length > 1 ? 's' : ''}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SQLImportModal;
